import {
  AdminCardContext,
  MultilingualPlacementPopupCard,
  PopupCardApiError,
  SingleLanguagePlacementPopupCard,
} from 'src/typings/placementPopupCard';

export const PlacementPopupCardAudience = {
  clientModule: 'client-module',
  adminPanel: 'admin-panel',
} as const;

export type PlacementPopupCardAudienceType = typeof PlacementPopupCardAudience[keyof typeof PlacementPopupCardAudience];

export const PlacementPopupCardContext = {
  crosstableSidebar: 'crosstableSidebar',
  planPopup: 'planPopup',
} as const;

interface FetchPopupCardOptions {
  lang?: string;
  signal?: AbortSignal;
  audience?: PlacementPopupCardAudienceType;
  accessToken?: string;
  cardContext?: AdminCardContext;
}

export class PlacementPopupCardRequestError extends Error {
  status: number;

  payload: PopupCardApiError | unknown;

  constructor(status: number, payload: PopupCardApiError | unknown) {
    super('Popup card request failed');
    this.name = 'PlacementPopupCardRequestError';
    this.status = status;
    this.payload = payload;
  }
}

const assertPopupCard = (payload: unknown, isAdminPanel: boolean): void => {
  const card = payload as Partial<SingleLanguagePlacementPopupCard> | null;

  if (!card || typeof card.id !== 'string' || !card.sections || !card.action) {
    throw new Error('Popup card response has an invalid shape');
  }
  if (isAdminPanel && !Object.prototype.hasOwnProperty.call(card, 'placementStatusBlock')) {
    throw new Error('Admin popup card response is missing placementStatusBlock');
  }
};

// TypeScript overloads keep the response text type tied to the presence of lang.
// eslint-disable-next-line no-redeclare
export async function fetchPlacementPopupCard(
  placementId: string,
  options: FetchPopupCardOptions & { lang: string },
): Promise<SingleLanguagePlacementPopupCard>;

// eslint-disable-next-line no-redeclare
export async function fetchPlacementPopupCard(
  placementId: string,
  options?: FetchPopupCardOptions & { lang?: undefined },
): Promise<MultilingualPlacementPopupCard>;

// eslint-disable-next-line no-redeclare
export async function fetchPlacementPopupCard(
  placementId: string,
  options: FetchPopupCardOptions = {},
): Promise<SingleLanguagePlacementPopupCard | MultilingualPlacementPopupCard> {
  const apiBaseUrl = process.env.REACT_APP_REST_API;
  if (!apiBaseUrl) throw new Error('REACT_APP_REST_API is not configured');

  const audience = options.audience || PlacementPopupCardAudience.clientModule;
  const isAdminPanel = audience === PlacementPopupCardAudience.adminPanel;
  if (isAdminPanel && !options.accessToken) {
    throw new Error('Access token is required for the admin popup card request');
  }

  const endpoint = isAdminPanel ? 'admin-pannel' : 'client-module';
  const path = `/api/cntrlr/${endpoint}/placements/${encodeURIComponent(placementId)}/popup-card`;
  const url = new URL(path, apiBaseUrl);

  if (options.lang !== undefined) {
    url.searchParams.set('lang', options.lang);
  }
  if (isAdminPanel && options.cardContext !== undefined) {
    url.searchParams.set('cardContext', options.cardContext);
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (isAdminPanel) {
    headers.Authorization = `Bearer ${options.accessToken}`;
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    signal: options.signal,
    headers,
    cache: 'no-store',
  });
  const responseText = await response.text();
  let payload: unknown;

  try {
    payload = JSON.parse(responseText);
  } catch {
    if (response.ok) {
      throw new Error('Expected a JSON popup card response');
    }
    payload = responseText;
  }

  if (!response.ok) {
    throw new PlacementPopupCardRequestError(response.status, payload);
  }

  assertPopupCard(payload, isAdminPanel);
  return payload as SingleLanguagePlacementPopupCard | MultilingualPlacementPopupCard;
}
